import axios from "axios";
import React, { useMemo, useRef } from "react";
import { useState, useEffect } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoSearchOutline } from "react-icons/io5";
import CountryData from "json/countries.json";
import useClickOutSideComponent from "../../../hooks/useClickOutsideComponent";

interface Country {
  name: string;
  code: string;
  flag: string;
}

interface Props {
  onSelect: (country: Country) => void;
  value?: string;
  error?: string;
  labelClassName?: string;
  labelText?: string;
  isRequired?: boolean;
  isDropdownAbove?: boolean;
}

const CountryDropdown: React.FC<Props> = ({
  onSelect,
  value,
  error,
  labelClassName,
  labelText,
  isRequired,
  isDropdownAbove,
}) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [search, setSearch] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useClickOutSideComponent(containerRef, () => setShowDropdown(false));

  useEffect(() => {
    setCountries(CountryData);
  }, []);

  const handleSelect = (country: Country) => {
    setSelectedCountry(country);
    setShowDropdown(false);
    onSelect(country);
  };

  const countriesFiltered = useMemo(() => {
    return countries.filter((c) =>
      c.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [countries, search]);

  useEffect(() => {
    if (value && countries.length > 0) {
      const country = countries.find((c) => c.name === value);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [value, countries]);

  return (
    <div className='relative mt-1' ref={containerRef}>
      {labelText && (
        <label
          className={` font-medium capitalize text-sm ${
            showDropdown ? "text-blueShades-4" : "text-ashShade-16"
          } ${labelClassName}`}
        >
          {labelText} {isRequired && <span className='text-ERROR'>*</span>}
        </label>
      )}

      <div
        className={`border cursor-pointer  ${showDropdown ? "border-[1.5px] border-blueShades-6 outline outline-[3px] outline-[#D8E9F080]" : error ? "border-[1.5px] border-ERROR outline outline-3px outline-[#FFE5E5]" : "border-blueShades-2"} rounded-md px-3 py-2 text-sm text-ashShade-16`}
        onClick={() => setShowDropdown(!showDropdown)}
      >
        {selectedCountry ? (
          <div className='flex items-center justify-between'>
            <div className='text-sm flex items-center'>
              <img
                src={selectedCountry.flag}
                alt='flag'
                className='w-6 h-4 mr-2'
              />
              <span>{selectedCountry.name}</span>
            </div>
            {showDropdown ? (
              <IoIosArrowUp size={16} className='text-ashShade-2' />
            ) : (
              <IoIosArrowDown size={16} className='text-ashShade-2' />
            )}
          </div>
        ) : (
          <p className='text-bash !font-SatoshiRegular text-base w-full justify-between flex items-center'>
            <span className='text-sm'>Select Country</span>
            <span>
              {showDropdown ? (
                <IoIosArrowUp size={16} className='text-ashShade-2' />
              ) : (
                <IoIosArrowDown size={16} className='text-ashShade-2' />
              )}
            </span>
          </p>
        )}
      </div>

      {showDropdown && (
        <div
          className={`absolute bg-white border rounded-md w-full z-10 shadow-md ${isDropdownAbove ? "bottom-full" : "top-full"} `}
        >
          <div
            className={`py-2 px-3 sticky right-0 left-0 border border-ashShade-0 `}
          >
            <div className='relative w-full'>
              <IoSearchOutline
                size={16}
                className='absolute left-3 top-[55%] -translate-y-1/2 text-bash'
              />
              <input
                type='text'
                placeholder='Search'
                className='w-full border bg-[#fcfcfd]  border-ashShade-0 rounded-md pl-8 pr-2 py-1 outline-none placeholder:text-bblack-1 placeholder:font-medium placeholder:text-sm'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className='max-h-52 overflow-y-auto'>
            {countriesFiltered.map((country) => (
              <div
                key={country.code}
                className={`py-2 px-3 flex items-center  hover:bg-bbg cursor-pointer font-SatoshiRegular text-base ${selectedCountry?.code === country.code && "bg-ashShade-13"}`}
                onClick={() => handleSelect(country)}
              >
                <img
                  src={country.flag}
                  alt='flag'
                  className='w-6 h-4 mr-2 rounded-[4px]'
                />
                {country.name}
              </div>
            ))}
          </div>
        </div>
      )}
      {error && (
        <span className='absolute left-0 top-full text-ERROR text-[13px] mb-4'>
          {error}
        </span>
      )}
    </div>
  );
};

export default CountryDropdown;
